import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Coins, Plus, Trash2 } from 'lucide-react'
import { api } from '../../lib/api'
import { centsToEurosInput, formatCents, parseEurosToCents } from '../../lib/money'
import type { InvoiceLineInput, RatePickerItem } from '@toga/shared'
import RatePickerModal from '../rates/RatePickerModal'

interface UiLine extends InvoiceLineInput {
  _key: string
  unitPriceInput: string // string editable mientras se escribe (12,50)
  costInput: string // coste interno editable (12,50) — nunca llega al cliente
  quantityInput: string
}

interface Props {
  value: InvoiceLineInput[]
  irpfRate: string // "0.15"
  fundsAppliedCents: number
  onChange: (lines: InvoiceLineInput[]) => void
  /** Cliente de la factura (necesario para el picker de tarifas). */
  clientId?: string | null
  /** Línea de servicio de la oportunidad (filtra el catálogo si se proporciona). */
  serviceLine?: string | null
}

export default function InvoiceEditor({
  value,
  irpfRate,
  fundsAppliedCents,
  onChange,
  clientId,
  serviceLine,
}: Props) {
  const { t } = useTranslation('billing')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [lines, setLines] = useState<UiLine[]>(() => value.map(toUi))

  // Si el padre actualiza lines (al recibir factura del servidor), sincronizamos.
  useEffect(() => {
    setLines(value.map(toUi))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.length])

  useEffect(() => {
    onChange(lines.map(stripUi))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines])

  const totals = useMemo(
    () => computeTotalsLocal(lines, irpfRate, fundsAppliedCents),
    [lines, irpfRate, fundsAppliedCents]
  )

  const update = (idx: number, patch: Partial<UiLine>) => {
    setLines((prev) => {
      const copy = [...prev]
      copy[idx] = { ...copy[idx]!, ...patch }
      return copy
    })
  }

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      toUi({
        position: prev.length,
        productId: null,
        description: '',
        quantity: '1',
        unitPriceCents: 0,
        costCents: 0,
        discountPct: '0',
        vatRate: '0.21',
        isSupplied: false,
      }),
    ])
  }

  const removeLine = (idx: number) => {
    setLines((prev) => prev.filter((_, i) => i !== idx))
  }

  const addLineFromRate = (it: RatePickerItem) => {
    setLines((prev) => [
      ...prev,
      toUi({
        position: prev.length,
        productId: null,
        description: it.name,
        quantity: '1',
        unitPriceCents: it.appliedAmountCents,
        costCents: 0,
        discountPct: '0',
        vatRate: it.vatRate.toString(),
        isSupplied: false,
      }),
    ])
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider t-faint">
            <tr>
              <th className="px-2 py-1 text-left">{t('editor.headers.description')}</th>
              <th className="px-2 py-1 text-right w-16">{t('editor.headers.quantity')}</th>
              <th className="px-2 py-1 text-right w-28">{t('editor.headers.price')}</th>
              <th className="px-2 py-1 text-right w-24">{t('editor.headers.vat')}</th>
              <th className="px-2 py-1 text-right w-28">{t('editor.headers.total')}</th>
              <th className="w-8"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, idx) => {
              const lineTotal = computeLineTotalCentsLocal(l)
              return (
                <tr key={l._key} className="border-t border-base/40">
                  <td className="px-2 py-1">
                    <input
                      value={l.description}
                      onChange={(e) => update(idx, { description: e.target.value })}
                      placeholder={t('editor.descriptionPlaceholder')}
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={l.quantityInput}
                      onChange={(e) =>
                        update(idx, {
                          quantityInput: e.target.value,
                          quantity: parseDecString(e.target.value),
                        })
                      }
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary text-right focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <input
                      value={l.unitPriceInput}
                      onChange={(e) =>
                        update(idx, {
                          unitPriceInput: e.target.value,
                          unitPriceCents: parseEurosToCents(e.target.value),
                        })
                      }
                      placeholder="0,00"
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary text-right font-mono focus:outline-none focus:border-toga-500/50"
                    />
                  </td>
                  <td className="px-2 py-1">
                    <select
                      value={l.vatRate}
                      onChange={(e) => update(idx, { vatRate: e.target.value })}
                      className="w-full bg-transparent border border-base rounded px-2 py-1 text-sm t-primary focus:outline-none focus:border-toga-500/50"
                    >
                      <option value="0.21">21%</option>
                      <option value="0.10">10%</option>
                      <option value="0.04">4%</option>
                      <option value="0">0%</option>
                    </select>
                  </td>
                  <td className="px-2 py-1 text-right t-primary font-mono">
                    {formatCents(lineTotal)}
                  </td>
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      onClick={() => removeLine(idx)}
                      className="w-7 h-7 grid place-items-center rounded hover:bg-rose-500/10 t-muted hover:text-rose-400"
                      title={t('editor.removeLine')}
                    >
                      <Trash2 size={13} />
                    </button>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={addLine}
          className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover flex items-center gap-1.5"
        >
          <Plus size={13} /> {t('editor.addLine')}
        </button>
        <button
          type="button"
          onClick={() => setPickerOpen(true)}
          disabled={!clientId}
          title={
            clientId
              ? t('editor.fromRateTitleEnabled')
              : t('editor.fromRateTitleDisabled')
          }
          className="px-3 py-1.5 rounded-lg text-xs t-secondary border border-base hover:bg-hover hover:text-toga-700 dark:hover:text-toga-200 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Coins size={13} /> {t('editor.fromRate')}
        </button>
      </div>

      <RatePickerModal
        open={pickerOpen}
        clientId={clientId ?? null}
        serviceLine={serviceLine ?? null}
        onClose={() => setPickerOpen(false)}
        onPick={addLineFromRate}
      />

      {/* Totales */}
      <div className="surface-deep rounded-lg p-4 max-w-md ml-auto text-sm">
        <Row label={t('editor.totals.taxBase')} value={formatCents(totals.subtotalCents)} />
        {totals.vatBreakdown.map((v) => (
          <Row
            key={v.rate}
            label={t('editor.totals.vatLine', { pct: (v.rate * 100).toFixed(0), base: formatCents(v.baseCents) })}
            value={formatCents(v.vatCents)}
          />
        ))}
        {totals.irpfCents > 0 && (
          <Row
            label={t('editor.totals.irpf', { pct: (Number(irpfRate) * 100).toFixed(2) })}
            value={`-${formatCents(totals.irpfCents)}`}
            tone="rose"
          />
        )}
        {fundsAppliedCents > 0 && (
          <Row
            label={t('editor.totals.fundsApplied')}
            value={`-${formatCents(fundsAppliedCents)}`}
            tone="emerald"
          />
        )}
        <div className="border-t border-base/40 mt-2 pt-2">
          <Row label={t('editor.totals.total')} value={formatCents(totals.totalCents)} bold />
        </div>
      </div>

    </div>
  )
}

function Row({
  label,
  value,
  bold,
  tone,
}: {
  label: string
  value: string
  bold?: boolean
  tone?: 'rose' | 'emerald'
}) {
  const cls = [
    'flex justify-between',
    bold ? 't-primary font-semibold text-base' : 't-secondary',
    tone === 'rose' ? 'text-rose-700 dark:text-rose-300' : '',
    tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-300' : '',
  ].join(' ')
  return (
    <div className={cls}>
      <span>{label}</span>
      <span className="font-mono">{value}</span>
    </div>
  )
}

// ---------- Helpers UI ----------

function toUi(line: InvoiceLineInput): UiLine {
  return {
    ...line,
    costCents: line.costCents ?? 0,
    _key: cryptoRandom(),
    quantityInput: line.quantity || '1',
    unitPriceInput: centsToEurosInput(line.unitPriceCents ?? 0),
    costInput: centsToEurosInput(line.costCents ?? 0),
  }
}

function stripUi(line: UiLine): InvoiceLineInput {
  const { _key, quantityInput, unitPriceInput, costInput, ...rest } = line
  void _key
  void quantityInput
  void unitPriceInput
  void costInput
  return rest
}

function parseDecString(s: string): string {
  return s.replace(',', '.').replace(/[^0-9.]/g, '') || '0'
}

function cryptoRandom(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

// ---------- Cálculo local (eco del backend) ----------

function computeLineTotalCentsLocal(line: UiLine): number {
  const qty = Number(line.quantity) || 0
  const unit = (line.unitPriceCents ?? 0) / 100
  const disc = Number(line.discountPct) || 0
  const gross = qty * unit
  const net = gross * (1 - disc)
  const vatBase = line.isSupplied ? 0 : net
  const vat = vatBase * (Number(line.vatRate) || 0)
  return Math.round((net + vat) * 100)
}

/** Margen interno de la línea = subtotal (neto sin IVA) − coste × cantidad. */
function computeLineMarginCentsLocal(line: UiLine): number {
  const qty = Number(line.quantity) || 0
  const disc = Number(line.discountPct) || 0
  const netCents = Math.round(qty * ((line.unitPriceCents ?? 0) / 100) * (1 - disc) * 100)
  const costCents = Math.round(qty * (line.costCents ?? 0))
  return netCents - costCents
}

function computeTotalsLocal(
  lines: UiLine[],
  irpfRate: string,
  fundsAppliedCents: number
): {
  subtotalCents: number
  irpfCents: number
  totalCents: number
  totalCostCents: number
  marginCents: number
  vatBreakdown: Array<{ rate: number; baseCents: number; vatCents: number }>
} {
  let subtotalCents = 0
  let totalCostCents = 0
  let netNonSupplied = 0
  const vatMap = new Map<number, { baseCents: number; vatCents: number }>()

  for (const l of lines) {
    const qty = Number(l.quantity) || 0
    const unit = (l.unitPriceCents ?? 0) / 100
    const disc = Number(l.discountPct) || 0
    const net = qty * unit * (1 - disc)
    const netCents = Math.round(net * 100)
    subtotalCents += netCents
    totalCostCents += Math.round(qty * (l.costCents ?? 0))
    if (!l.isSupplied) {
      netNonSupplied += net
      const rate = Number(l.vatRate) || 0
      const vat = net * rate
      const prev = vatMap.get(rate) ?? { baseCents: 0, vatCents: 0 }
      vatMap.set(rate, {
        baseCents: prev.baseCents + netCents,
        vatCents: prev.vatCents + Math.round(vat * 100),
      })
    }
  }

  const irpf = netNonSupplied * (Number(irpfRate) || 0)
  const irpfCents = Math.round(irpf * 100)
  const vatTotal = Array.from(vatMap.values()).reduce((a, b) => a + b.vatCents, 0)
  const totalCents = subtotalCents + vatTotal - irpfCents - Math.max(0, fundsAppliedCents)

  const vatBreakdown = Array.from(vatMap.entries())
    .map(([rate, v]) => ({ rate, baseCents: v.baseCents, vatCents: v.vatCents }))
    .sort((a, b) => b.rate - a.rate)

  return {
    subtotalCents,
    irpfCents,
    totalCents,
    totalCostCents,
    marginCents: subtotalCents - totalCostCents,
    vatBreakdown,
  }
}
